
// @ts-nocheck - disable typescript due to to many type errors

// Packages
import { defineComponent, PropType } from 'vue';
import { mapActions, mapState } from 'pinia';
import { PortalTarget } from 'portal-vue';
import debounce from 'lodash/debounce';

// Helpers
import { smoothScroll } from '@white-label-helper/scroll';
import { email, notNumber, required, requiredIf, sameAs, maxLength, noSpacesOrSpecialCharacters } from '@white-label-helper/vuelidate';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getAppPaymentProvider } from '@white-label-helper/get-app-payment-provider';
import { isBookingPortal } from '@white-label-helper/is-booking-portal';
import { trackUserIdentity } from '@white-label-helper/ga-tracking';
import { format, utcToZonedTime, DATE_TIME_FORMATS } from '@white-label-helper/date-utilities';

// Constants
import {
  PAYMENT_PROVIDERS,
  TEXT_INPUT_DEBOUNCE_TIME,
  VEHICLE_OCCUPANTS,
  CANT_FIND_FLIGHT_ID,
  PRODUCT_TYPES,
  FEATURE_FLAGS,
} from '@white-label-configuration/constants';

// Mixins
import checkoutForm from '@white-label-helper/mixin-checkout-form';

// Store
import { useDiscountCodeStore } from '@white-label-store/discount-code';

import { useMarketingPreferencesStore } from '@white-label-store/marketing-preferences';
import { usePaymentFormStore } from '@white-label-store/payment-form';
import { useCartCheckoutStore } from '@white-label-store/cart-checkout';
import { useUserInfoStore } from '@white-label-store/user-info';

// Components
import IconMessage from '../icon-message/icon-message.vue';
import TextField from '../text-field/text-field.vue';
import Button from '../button/button.vue';
import PaymentPolicies from '../payment-policies/payment-policies.vue';
import DiscountCode from '../discount-code/discount-code.vue';
import PaymentGatewayBraintree from '../payment-gateway-braintree/payment-gateway-braintree.vue';
import PaymentGatewayStripe from '../payment-gateway-stripe/payment-gateway-stripe.vue';
import PaymentGatewayWindcave from '../payment-gateway-windcave/payment-gateway-windcave.vue';
import PaymentMarketingChannels from '../payment-marketing-channels/payment-marketing-channels.vue';
import CancellationAndAmendmentPolicy from '../cancellation-and-amendment-policy/cancellation-and-amendment-policy.vue';
import DropdownWrapper from '../dropdown-wrapper/dropdown-wrapper.vue';
import FlightLookup from '../flight-lookup/flight-lookup.vue';

// Types
import type VueI18n from 'vue-i18n';
import type { CancellationPolicy, AmendmentPolicy } from '@white-label-types/parking-booking';
import type {
  BraintreeConfig,
  CartItem,
  ParsedDiscount,
  Discount,
  StripeConfig,
  Total,
  CancellationProtection,
  WindcaveConfig,
} from '@white-label-types/parking-checkout';
import type { UserData, MetaData } from '@white-label-types/stores';
import Checkbox from '../checkbox/checkbox.vue';
import PhoneField from '../phone-field/phone-field.vue';

import { datadogRum } from '@datadog/browser-rum';
import { getFeatureFlag } from '@white-label-plugin/launch-darkly';
import { getAppHeroProduct } from '@white-label-helper/get-app-hero-product';
import LookupConfirmation from '../lookup-confirmation/lookup-confirmation.vue';
import Tooltip from '../tooltip/tooltip.vue';
export default defineComponent({
  components: {
    ButtonControl: Button,
    Policies: PaymentPolicies,
    Checkbox: Checkbox,
    PaymentGatewaysSharedBraintree: PaymentGatewayBraintree,
    PaymentGatewaysSharedStripe: PaymentGatewayStripe,
    PaymentGatewaysSharedWindcave: PaymentGatewayWindcave,
    IconArrow: () => import('@white-label-icon/icon-arrow'),
    IconAlert: () => import('@white-label-icon/icon-alert'),
    IconLockStar: () => import('@white-label-icon/icon-lock-star'),
    IconAskMark: () => import('@white-label-icon/icon-ask-mark'),
    IconMessage,
    DiscountCode: DiscountCode,
    TextField,
    PhoneField,
    MarketingChannels: PaymentMarketingChannels,
    PortalTarget,
    CancellationAndAmendmentPolicy,
    DropdownWrapper,
    FlightLookup,
    LookupConfirmation,
    Tooltip
  },

  mixins: [checkoutForm],

  props: {
    title: {
      type: String,
      default: ''
    },
    cancellationPolicies: {
      type: Array as PropType<CancellationPolicy[] | CartItem['inventory_option']['cancellation_policies'][]>,
      default: () => []
    },
    amendmentPolicies: {
      type: Array as PropType<AmendmentPolicy[] | CartItem['inventory_option']['amendment_policies'][]>,
      default: () => []
    },
    paymentsConfig: {
      type: Object as PropType<BraintreeConfig | StripeConfig | WindcaveConfig>,
      required: true,
      default: () => ({
        token: '',
        apiKey: ''
      })
    },
    paymentsConfigReceived: {
      type: Boolean,
      required: true,
      default: false
    },
    paymentIntentUpdated: {
      type: Boolean,
      default: false
    },
    orderTotal: {
      type: Number,
      default: 0
    },
    cartToken: {
      type: String,
      default: '',
    },
    isProtected: {
      type: Boolean,
      required: true,
    },
    cancellationProtection: {
      type: Array as PropType<CancellationProtection[]>,
      required: true,
    },
    isBookingFeeCharged: {
      type: Boolean,
      required: true,
      default: false,
    },
    cartItems: {
      type: Array as PropType<CartItem[]>,
      default: () => [] as CartItem[],
    },
  },

  validations() {
    const userDetailsValidations = {
      first_name: {
        required,
        notNumber
      },
      last_name: {
        required,
        notNumber
      },
      license_plate: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('plate_number');
        }),
        noSpacesOrSpecialCharacters:  function(value) {
          if (!value) return true;
          return noSpacesOrSpecialCharacters(value);
        },
      },

      number_of_people_in_vehicle: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('number_of_people_in_vehicle') && this.useNewVehicleLayout;
        })
      },
      number_of_child_seats: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('is_child_seat_required') && this.useNewVehicleLayout;
        })
      },
      email: {
        required,
        email
      },
      phone: {
        phone_number: {
          required: requiredIf(() => {
            // @ts-ignore - `this` is not recognized in its context
            if (!this.checkoutForm_checkFieldVisibility('phone_number')) return false;
            // @ts-ignore - `this` is not recognized in its context
            return this.checkoutForm_checkIfFieldRequired('phone_number') || this.userDetails.marketing_channels?.sms;
          }),

          isValid: () : boolean => {
            const isEmpty = !this.userDetails.phone?.phone_number?.length;
            return !isEmpty ? this.phoneValid : true;
          }
        }
      },
      zipcode: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('zipcode');
        })
      },
      outbound_flight: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired(
            'outbound_flight_number'
          );
        })
      },
      inbound_flight: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired(
            'inbound_flight_number'
          );
        })
      },
      ...(isBookingPortal
        ? {
          others_email: {
            email
          },
          booker_email: {
            // @ts-ignore - `this` is not recognized in its context
            required: requiredIf(() => isBookingPortal),
            email
          }
        }
        : {})
    };



    if(this.isVehicleFieldsFeatureFlagEnabled && (!this.displayVehicleLookUp || this.enterManually)) {
      userDetailsValidations.vehicle_make = {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('vehicle_make');
        }),
        maxLength: maxLength(this.vehicleFieldsMaxLength)

      }
      userDetailsValidations.vehicle_model = {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('vehicle_model');
        }),
        maxLength: maxLength(this.vehicleFieldsMaxLength)
      }
      userDetailsValidations.vehicle_colour= {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('vehicle_colour');
        }),
        maxLength: maxLength(this.vehicleFieldsMaxLength)
      }
    }


    const validations = {
      userDetails: {
        ...userDetailsValidations
      },
      emailConfirmation: {
        noPaste(): boolean {
          // @ts-ignore - `this` is not recognized in its context
          return !this.attemptToPaste;
        },
        sameAs: sameAs(function sameAsEmail() {
          // @ts-ignore - `this` is not recognized in its context
          return this.userDetails.email;
        }),
        required
      }
    };
    return validations;
  },

  data() {
    const defaultData = {
      disableButton: false,
      userDetails: {
        first_name: '',
        last_name: '',
        email: '',
        phone: {
          phone_number: '',
          formatted_number: '',
          country_code: '',
          country_iso_code: ''
        },
        license_plate: '',
        number_of_people_in_vehicle: null,
        number_of_child_seats: null,
        zipcode: '',
        outbound_flight: '',
        outbound_airline: '',
        outbound_airport: '',
        outbound_terminal: '',
        inbound_flight: '',
        inbound_airline: '',
        inbound_airport: '',
        inbound_terminal: '',
        marketing_channels: {},
        booker_email: '',
        others_email: '',
        send_booker_email: false,
        send_customer_email: false,
        send_others_email: false,
      } as UserData,
      metaData: {
        cost_centre: ''
      } as MetaData,
      emailConfirmation: '',
      attemptToPaste: false,
      phoneValid: false,
      showDiscountCode: false,
      validatingDiscount: false,
      discountError: '',
      onEmailChangeDebounced: null as (() => void) | null,
      showMarketingPreferences: false,
      isBookingPortal,
      booker_email: '',
      others_email: '',
      isNewUser: false,
      showDropdownExpanded: false,
      numberInVehicleOpened: false,
      numberChildSeatsOpened: false,
      vehicleLookUpError: false,
      vehicleLookUpLoading: false,
      enterManually: false,
      showDetails: false,
      flightLookupTooltipVisible: false,
    }
    if(this.$launchDarkly.variation('GT_2824_DAILY_HOURLY_BOOKING_REPORTS_ADDITIONAL_INFORMATION') === true) {
      defaultData.userDetails.vehicle_make= ''
      defaultData.userDetails.vehicle_model= ''
      defaultData.userDetails.vehicle_colour= ''
    }
    return defaultData;
  },

  computed: {
    ...mapState(useUserInfoStore, {
      getAllUserDetails: 'readUserDetails',
      inboundFlightLookupItems: 'readInboundFlightLookupItems',
      outboundFlightLookupItems: 'readOutboundFlightLookupItems',
      inboundFlightSelected: 'readInboundFlightLookupSelected',
      outboundFlightSelected: 'readOutboundFlightLookupSelected',
      findOutboundFlightById: 'findOutboundFlightById',
    }),

    ...mapState(useMarketingPreferencesStore, {
      readMarketingPreferences: 'readMarketingPreferences',
      readUserMarketingSubscription: 'readUserMarketingSubscription',
      readMarketingStrategy: 'readMarketingStrategy',
      readCustomerUUID: 'readCustomerUUID',
    }),

    ...mapState(useCartCheckoutStore, {
      readCartItemsType: 'readCartItemsType',
      readCartItems: 'readCartItems',
    }),

    ...mapState(usePaymentFormStore, {
      readPaymentFormData: 'readPaymentFormData',
    }),

    ...mapState(useDiscountCodeStore, {
      discountObject: 'discountObject',
    }),

    paymentSummaryDropdown(): boolean {
      return getFeatureFlag(FEATURE_FLAGS.CRO_840_ORDER_SUMMARY_DROPDOWN) === true;
    },

    emailError(): any {
      // @ts-ignore
      if (
        !this.$v.emailConfirmation.sameAs &&
        !this.$v.userDetails.email.$error
      ) {
        // @ts-ignore
        return this.$v.emailConfirmation;
      }
      // @ts-ignore
      return this.$v.userDetails.email;
    },


    displayEmailErrorMessage(): boolean {
      // @ts-ignore
      return (
        this.$v.userDetails.email.$error && this.$v.emailConfirmation.noPaste
      );
    },

    displayBraintreeGateway(): boolean {
      return (
        !isBookingPortal &&
        getAppPaymentProvider() === PAYMENT_PROVIDERS.BRAINTREE &&
        this.paymentsConfigReceived &&
        this.paymentNeeded
      );
    },

    displayStripeGateway(): boolean {
      return (
        !isBookingPortal &&
        getAppPaymentProvider() === PAYMENT_PROVIDERS.STRIPE &&
        this.paymentsConfigReceived &&
        this.paymentNeeded
      );
    },

    displayWindcaveGateway(): boolean {
      const isWindcavePayment = !isBookingPortal &&
        this.$launchDarkly.variation(FEATURE_FLAGS.ENABLE_WINDCAVE_SETUP) &&
        getAppPaymentProvider() === PAYMENT_PROVIDERS.WINDCAVE;
      return isWindcavePayment && this.paymentsConfigReceived && this.paymentNeeded;
    },
    displayOutboundFlightLookup() {
      return this.checkoutForm_checkFieldVisibility('outbound_flight_number') &&
              this.outboundFlightSelected !== null &&
              this.outboundFlightSelected.id !== CANT_FIND_FLIGHT_ID;
    },
    displayInfoMessage(): boolean {
      return (
        // @ts-ignore - Mixin method
        this.checkoutForm_paymentMethod ===
        this.checkoutForm_PAYMENT_METHODS.BRAINTREE.PAYPAL &&
        this.paymentNeeded
      );
    },

    iconArrowClass(): object {
      return { 'transform rotate-180': this.showDiscountCode };
    },

    submitButtonLabel(): VueI18n.TranslateResult {
      if (!this.paymentNeeded) {
        return this.$t('UI.button.payForBooking');
      }

      // @ts-ignore - Mixin method
      if (
        this.checkoutForm_paymentMethod ===
        this.checkoutForm_PAYMENT_METHODS.BRAINTREE.PAYPAL
      ) {
        return this.$t('UI.button.paypal');
      }

      if (isBookingPortal) {
        return this.$t('UI.button.makeBooking');
      }
      return this.$t('UI.button.payForBooking');
    },

    submitButtonId(): string {
      // @ts-ignore - Mixin method
      if (
        this.checkoutForm_paymentMethod ===
        this.checkoutForm_PAYMENT_METHODS.BRAINTREE.PAYPAL
      ) {
        return 'GAPaymentSubmitPaypal';
      }
      return 'GAPaymentSubmitCard';
    },

    submitButtonDisabled(): boolean {
      // @ts-ignore
      return this.$v.$error || this.disableButton;
    },

    paymentNeeded(): boolean {
      return this.orderTotal !== 0;
    },

    isDiscountFieldEnabled(): boolean {
      return getAppVariable(
        'discount_visibility_rules.is_enabled_in_payment_page'
      );
    },

    userMarketingSubscription() {
      return this.readUserMarketingSubscription;
    },

    marketingStrategy() {
      return this.readMarketingStrategy;
    },

    partnerMarketingPreferences() {
      return this.readMarketingPreferences;
    },

    cartItemsType() {
      return this.readCartItemsType;
    },
    isBookerEmailCheckboxDisabled() {
      return !(this.userDetails.booker_email && this.userDetails.booker_email.length)
    },
    isCustomerEmailCheckboxDisabled() {
      return !(this.userDetails.email && this.userDetails.email.length)
    },
    isOthersEmailCheckboxDisabled() {
      return !(this.userDetails.others_email && this.userDetails.others_email.length)
    },

    /**
     * Returns the email data from Vuex store userInfo
     */
    getUserInfoEmail() {
      return this.getAllUserDetails.email;
    },

    /**
     * Returns the first name data from Vuex store userInfo
     */
    getUserInfoFirstName() {
      return this.getAllUserDetails.firstName;
    },

    /**
     * Returns the last name data from Vuex store userInfo
     */
    getUserInfoLastName() {
      return this.getAllUserDetails.lastName;
    },

    /**
     * Returns the postal code data from Vuex store userInfo
     */
    getUserPostalCode() {
      return this.getAllUserDetails.postalCode;
    },

    /**
     * Returns the license plate data from Vuex store userInfo
     */
    getUserInfoLicense() {
      return this.getAllUserDetails.vehicleDetails.registrationNumber;
    },
    /**
     * Returns the vehicle make data from Vuex store userInfo
     */
    getUserInfoVehicleMake() {
      return this.getAllUserDetails.vehicleDetails.make;
    },
    /**
     * Returns the vehicle model data from Vuex store userInfo
     */
    getUserInfoVehicleModel() {
      return this.getAllUserDetails.vehicleDetails.model;
    },
    /**
     * Returns the vehicle colour data from Vuex store userInfo
     */
    getUserInfoVehicleColour() {
      return this.getAllUserDetails.vehicleDetails.colour;
    },

    /**
     * Returns the phone number data from Vuex store userInfo
     */
    getPhoneNumbers() {
      return this.getAllUserDetails.phoneDetails;
    },

    displayVehicleDetails(): boolean {
      return this.checkoutForm_checkFieldVisibility('plate_number')
      || this.checkoutForm_checkFieldVisibility('number_of_people_in_vehicle')
      || this.checkoutForm_checkFieldVisibility('vehicle_make')
      || this.checkoutForm_checkFieldVisibility('vehicle_model')
      || this.checkoutForm_checkFieldVisibility('vehicle_colour');
    },

    displayFlightLookup(): boolean {
      const isParkingProduct = getAppHeroProduct(PRODUCT_TYPES.PARKING) === PRODUCT_TYPES.PARKING;
      const displayFlightLookupForParking = isParkingProduct && getFeatureFlag(FEATURE_FLAGS.PT_2857_FLIGHT_LOOKUP_PARKING) === true;
      return getFeatureFlag(FEATURE_FLAGS.PT_2857_FLIGHT_LOOKUP) === true
        && getAppVariable('is_flight_lookup_enabled')
        && displayFlightLookupForParking
        && (this.checkoutForm_checkFieldVisibility('inbound_flight_number') || this.checkoutForm_checkFieldVisibility('outbound_flight_number'));
    },
    numberInVehicleOptions(): { id: number | string, name: string }[] {
      const options = [...VEHICLE_OCCUPANTS];
      // Number in vehicle can't be 0
      return options.slice(1);
    },

    numberChildSeatsOptions(): { id: number | string, name: string }[] {
      const options = [...VEHICLE_OCCUPANTS];
      // Number of child seats can't be more than (number in vehicle -1)
      if (this.userDetails.number_of_people_in_vehicle) {
        return options.slice(0, this.userDetails.number_of_people_in_vehicle);
      }
      return [];
    },

    computedNumberInVehicle: {
      get(): {id: number | string, name: string} {
        return (
          this.numberInVehicleOptions.find(
            (item) => item.name === this.userDetails.number_of_people_in_vehicle?.toString()
          ) || {
            name: '',
            id: '',
          }
        );
      },
      set(val: {id: number, name: string}): void {
        this.userDetails.number_of_people_in_vehicle = parseInt(val.name);
        if (this.userDetails.number_of_people_in_vehicle === 1) {
          this.userDetails.number_of_child_seats = 0;
        } else {
          this.userDetails.number_of_child_seats = null;
        }
      }
    },

    computedNumberChildSeats: {
      get(): {id: number | string, name: string} {
        return (
          this.numberChildSeatsOptions.find(
            (item) => item.name === this.userDetails.number_of_child_seats?.toString()
          ) || {
            name: '',
            id: '',
          }
        );
      },
      set(val: {id: number, name: string}): void {
        this.userDetails.number_of_child_seats = parseInt(val.name);
      }
    },

    disableChildSeatsDropdown(): boolean {
      return !this.userDetails.number_of_people_in_vehicle;
    },

    numberInVehicleError(): boolean {
      // @ts-ignore
      return this.$v.userDetails.number_of_people_in_vehicle.$error;
    },

    numberChildSeatsError(): boolean {
      // @ts-ignore
      return this.$v.userDetails.number_of_child_seats.$error;
    },

    useNewVehicleLayout(): boolean {
      return this.$launchDarkly.variation('ECOM-2380_VEHICLE_DETAILS') === true;
    },

    isVehicleFieldsFeatureFlagEnabled():boolean {
      return this.$launchDarkly.variation('GT_2824_DAILY_HOURLY_BOOKING_REPORTS_ADDITIONAL_INFORMATION') === true
    },

    isVehicleFeatureFlagEnabled():boolean {
      return this.$launchDarkly.variation(FEATURE_FLAGS.PT_2971_VEHICLE_LOOKUP_IMPROVED_UI) === true;
    },

    vehicleFieldsMaxLength(): number {
      return 25
    },
    displayVehicleLookUp(): boolean {
      return getAppVariable('is_vehicle_lookup_enabled') && this.checkoutForm_checkFieldVisibility('plate_number') && this.isVehicleFeatureFlagEnabled;
    },
    filteredLookupDetails() {
      const details = [
        this.checkoutForm_checkFieldVisibility('vehicle_make') ? this.userDetails.vehicle_make : null,
        this.checkoutForm_checkFieldVisibility('vehicle_model') ? this.userDetails.vehicle_model : null,
        this.checkoutForm_checkFieldVisibility('vehicle_colour') ? this.userDetails.vehicle_colour : null,
      ].filter(Boolean);
      return details.length > 0 ? details : null;
    },
  },

  watch: {
    userDetails: {
      deep: true, // watch nested data properties
      handler(formData: UserData) {
        this.commitStoreFormData({ formData });

        // Uncheck recipients checkbox if email value is empty
        if(!formData.email) {
          this.userDetails.send_customer_email=false
        }
        if(!formData.others_email) {
          this.userDetails.send_others_email=false
        }
      },
    },
  },

  async mounted() {
    if (this.$auth.user) {
      // If user is logged in retrieve data from userInfo store to populate fields
      await this.getUserData();
    } else {
      this.loadStoredData();
    }

    this.onEmailChangeDebounced = debounce(
      this.handleEmailChange,
      TEXT_INPUT_DEBOUNCE_TIME
    );

    if (isBookingPortal) {
      this.handleEmailBookerChange();
    }

    if (this.displayFlightLookup && (this.getSearchCriteria('arrival') || this.getSearchCriteria('departure'))) {
      if (this.checkoutForm_checkFieldVisibility('inbound_flight_number')) {
        await this.fetchFlightLookupDetails({...this.getSearchCriteria('arrival'), direction: 'arrival' });
      }
      if (this.checkoutForm_checkFieldVisibility('outbound_flight_number')) {
        await this.fetchFlightLookupDetails({...this.getSearchCriteria('departure'), direction: 'departure' });
      }

      this.prefillOutboundFlight();
    }
  },

  methods: {
    ...mapActions(useUserInfoStore, {
      fetchUserDetails: 'fetchUserDetails',
      fetchVehicleDetails: 'fetchVehicleDetails',
      fetchFlightLookupDetails: 'fetchFlightLookupDetails',
      storeInboundFlightLookup: 'selectInboundFlightLookup',
      storeOutboundFlightLookup: 'selectOutboundFlightLookup',
    }),

    ...mapActions(useMarketingPreferencesStore, {
      dispatchUserMarketingSubscription: 'dispatchUserMarketingSubscription',
    }),

    ...mapActions(useCartCheckoutStore, {
      commitCartBundle: 'commitCartBundle',
    }),

    ...mapActions(useDiscountCodeStore, {
      validateDiscountCode: 'validateDiscountCode',
      removeDiscountCode: 'removeDiscountCode',
    }),

    ...mapActions(usePaymentFormStore, {
      commitClearFormData: 'commitClearFormData',
      commitStoreFormData: 'commitStoreFormData',
    }),

    prefillOutboundFlight(): void {
      const flightWasSelected = this.readCartItems[0].search_criteria[this.cartItemsType].is_flight_selected;
      if (flightWasSelected) {
        const iata = this.readCartItems[0].search_criteria[this.cartItemsType].airline_iata;
        const flightNumber = this.readCartItems[0].search_criteria[this.cartItemsType].flight_number;
        const flightId = iata && flightNumber ? `${iata}-${flightNumber}` : null;
        const selectedOutboundFlight = flightId ? this.findOutboundFlightById(flightId) : { id: CANT_FIND_FLIGHT_ID };
        this.selectOutboundFlightLookup(selectedOutboundFlight);
      } else {
        this.selectOutboundFlightLookup({ id: CANT_FIND_FLIGHT_ID });
      }
    },

    selectOutboundFlightLookup(flightLookup): void {
      this.userDetails.outbound_flight = flightLookup?.id ?? '';
      this.userDetails.outbound_airline = flightLookup?.airlineName ?? '';
      this.userDetails.outbound_airport = flightLookup?.airport ?? '';
      this.userDetails.outbound_terminal = flightLookup?.terminalName ?? '';
      this.storeOutboundFlightLookup(flightLookup);
    },
    selectInboundFlightLookup(flightLookup): void {
      this.userDetails.inbound_flight = flightLookup?.id ?? '';
      this.userDetails.inbound_airline = flightLookup?.airlineName ?? '';
      this.userDetails.inbound_airport = flightLookup?.airport ?? '';
      this.userDetails.inbound_terminal = flightLookup?.terminalName ?? '';
      this.storeInboundFlightLookup(flightLookup);
    },

    onFormFieldBlur(field: string): void {
      if (field === 'license_plate') {
        this.vehicleLookUpError = false;
      }

      if (field.startsWith('email') && this.onEmailChangeDebounced) {
        this.onEmailChangeDebounced();
      }

      if (field === 'emailConfirmation') {
        this.$v[field].$touch();
      } else {
        this.$v.userDetails[field].$touch();
      }
    },
    onPaste(): void {
      this.attemptToPaste = true;
      // @ts-ignore
      this.$v.emailConfirmation.$touch();
      setTimeout(() => {
        this.attemptToPaste = false;
        // @ts-ignore
        if (!this.$v.$error) {
          // @ts-ignore
          this.$v.emailConfirmation.$reset();
        }
      }, 3000);
    },

    handleUserMarketingChannelsChange(marketingChannels: UserData['marketing_channels']): void {
      this.userDetails.marketing_channels = marketingChannels;
    },

    handleEmailBookerChange(): void {
      const userDetails = this.$auth;
      this.userDetails.booker_email = userDetails.user.email;
    },

    handleEmailChange() {
      // @ts-ignore - no access to $v
      if (
        !this.$v.userDetails.email.$invalid &&
        this.userDetails.email === this.emailConfirmation
      ) {
        let customerUUID: string | undefined;

        this.dispatchUserMarketingSubscription(this.userDetails.email)
          .then((data) => {
            // Check if new user
            this.isNewUser = (data === null);

            if (this.userDetails.marketing_channels) {
              this.updateDefaultMarketingChannelsUserValues();

              /**
               * Toggles the expansion state of the marketing pref dropdown
               * based on the availability of the phone number field and user preferences.
               */
              if (!this.isNewUser) {
                const { sms, email } = this.userDetails.marketing_channels;
                const phoneNumberFieldVisible = this.checkoutForm_checkFieldVisibility('phone_number');
                this.showDropdownExpanded = phoneNumberFieldVisible ? !(sms && email) : !email;

                customerUUID = this.readCustomerUUID;
              }
            }

            this.showMarketingPreferences = true;
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            trackUserIdentity(this.$gtm, this.userDetails.email, customerUUID);
          })
      }
    },
    /**
     * Set default marketing preferences according to
     * opt-in/opt-out startegy for new user and already saved settings
     * for an old user
     */
    updateDefaultMarketingChannelsUserValues() {
      const marketingChannels: UserData['marketing_channels'] = {};
      const optIn = this.marketingStrategy === "opt-in";
      const savedUserMarketingChannels = this.userMarketingSubscription?.marketing_channels;

      if (this.isMarketingChannelAvailable('email')) {
        marketingChannels.email = this.isNewUser ?
          !optIn :
          !!savedUserMarketingChannels?.email;
      }

      if (this.isMarketingChannelAvailable('sms')) {
        marketingChannels.sms = this.isNewUser ?
          !optIn :
          !!savedUserMarketingChannels?.sms;
      }

      this.userDetails = {
        ...this.userDetails,
        marketing_channels: marketingChannels
      };
    },

    isMarketingChannelAvailable(channel: string): boolean {
      return !!this.partnerMarketingPreferences?.marketing_channels?.includes(
        channel
      );
    },

    /*
    * Samuel Bruton
    * Confirming with another dev on what this actually is being used for.
    * Looks like it can be removed.
    * */
    loadStoredData(): void {
      const formData = this.readPaymentFormData;
      if (formData) {
        this.userDetails = {
          ...formData,
          // @ts-ignore mixin method
          phone: this.checkoutForm_checkFieldVisibility('phone_number')
            ? { ...formData.phone }
            : { ...this.userDetails.phone },
          // @ts-ignore mixin method
          license_plate: this.checkoutForm_checkFieldVisibility('plate_number')
            ? formData.license_plate
            : '',
          // @ts-ignore mixin method
          zipcode: this.checkoutForm_checkFieldVisibility('zipcode')
            ? formData.zipcode
            : '',
          // @ts-ignore mixin method
          outbound_flight: this.checkoutForm_checkFieldVisibility('outbound_flight_number')
            ? formData.outbound_flight
            : '',
          // @ts-ignore mixin method
          inbound_flight: this.checkoutForm_checkFieldVisibility('inbound_flight_number')
            ? formData.inbound_flight
            : '',
          // @ts-ignore mixin method
          number_of_people_in_vehicle: this.checkoutForm_checkFieldVisibility('number_of_people_in_vehicle')
            ? formData.number_of_people_in_vehicle
            : null,
          // @ts-ignore mixin method
          number_of_child_seats: this.checkoutForm_checkFieldVisibility('is_child_seat_required')
            ? formData.number_of_child_seats
            : null
        };

        if(this.isVehicleFieldsFeatureFlagEnabled) {
          this.userDetails.vehicle_make = this.checkoutForm_checkFieldVisibility('vehicle_make')
            ? formData.vehicle_make
            : ''
          this.userDetails.vehicle_model = this.checkoutForm_checkFieldVisibility('vehicle_model')
            ? formData.vehicle_model
            : ''
          this.userDetails.vehicle_colour = this.checkoutForm_checkFieldVisibility('vehicle_colour')
            ? formData.vehicle_colour
            : ''
        }
      }
    },

    /** API call to get the user data */
    async getUserData(): Promise<void> {
      const token = await this.$auth.getToken();
      if (token) {
        await this.fetchUserDetails(token)
          .then(() => {
            const formData = this.readPaymentFormData ?? {} as UserData;

            const getDataFromUserOrStore = (field: string, loggedInUserData: string) => {
              return formData[field] ? formData[field] : loggedInUserData;
            }

            this.userDetails = {
              ...formData,
              email: getDataFromUserOrStore('email', this.getUserInfoEmail),
              first_name: getDataFromUserOrStore('first_name', this.getUserInfoFirstName),
              last_name: getDataFromUserOrStore('last_name', this.getUserInfoLastName),
              // @ts-ignore mixin method
              license_plate: this.checkoutForm_checkFieldVisibility('plate_number')
                ? getDataFromUserOrStore('plate_namber', this.getUserInfoLicense)
                : '',
              // @ts-ignore mixin method
              zipcode: this.checkoutForm_checkFieldVisibility('zipcode')
                ? getDataFromUserOrStore('zipcode', this.getUserPostalCode)
                : '',
              // @ts-ignore mixin method
              phone: this.checkoutForm_checkFieldVisibility('phone_number')
                ? formData?.phone?.phone_number
                  ? { ...formData.phone }
                  : {
                      phone_number: this.getPhoneNumbers.phoneNumber,
                      formatted_number: this.getPhoneNumbers.phoneNumberWithDialingCode,
                      country_code: this.getPhoneNumbers.dialingCode,
                      country_iso_code: this.getPhoneNumbers.isoCode,
                    }
                : {
                  // empty data needed otherwise api call will fail
                    phone_number: '',
                    formatted_number: '',
                    country_code: '',
                    country_iso_code: '',
                },
              // @ts-ignore mixin method
              outbound_flight: this.checkoutForm_checkFieldVisibility('outbound_flight_number') && formData?.outbound_flight
                ? formData.outbound_flight
                : '',
              // @ts-ignore mixin method
              inbound_flight: this.checkoutForm_checkFieldVisibility('inbound_flight_number') && formData?.inbound_flight
                ? formData.inbound_flight
                : '',
              // @ts-ignore mixin method
              number_of_people_in_vehicle: this.checkoutForm_checkFieldVisibility('number_of_people_in_vehicle')  && formData?.number_of_people_in_vehicle
                ? formData.number_of_people_in_vehicle
                : null,
              // @ts-ignore mixin method
              number_of_child_seats: this.checkoutForm_checkFieldVisibility('is_child_seat_required') && formData?.number_of_child_seats
                ? formData.number_of_child_seats
                : null,
              marketing_channels: this.partnerMarketingPreferences
            };

            if(this.isVehicleFieldsFeatureFlagEnabled) {
            // @ts-ignore mixin method
            this.userDetails.vehicle_make = this.checkoutForm_checkFieldVisibility('vehicle_make')
              ? getDataFromUserOrStore('vehicle_make', this.getUserInfoVehicleMake)
              : ''
            // @ts-ignore mixin method
            this.userDetails.vehicle_model = this.checkoutForm_checkFieldVisibility('vehicle_model')
              ? getDataFromUserOrStore('vehicle_model', this.getUserInfoVehicleModel)
              : ''
            // @ts-ignore mixin method
            this.userDetails.vehicle_colour = this.checkoutForm_checkFieldVisibility('vehicle_colour')
              ? getDataFromUserOrStore('vehicle_colour', this.getUserInfoVehicleColour)
              : ''
            }
          })
          .catch((error: Error) => {
            console.error('Error with fetchUserDetails. Error logged with Datadog.')
            datadogRum.addError(error);
          })
      }
    },

    validateForm(): void {
      // @ts-ignore
      this.$v.$touch();
      this.enterManually = true;
      // @ts-ignore
      if (this.$v.$error) {
        smoothScroll('.input-container--error');
        return;
      }

      if(!this.displayWindcaveGateway) {
        this.setSubmitButtonDisableState(true);
      }

      if (!this.paymentNeeded || isBookingPortal) {
        // @ts-ignore - Mixin method
        this.checkoutForm_handlePaymentCompleted({
          payment_method_id: 'zero',
          merchant_id: 'zero'
        });
        this.setSubmitButtonDisableState(false);
      } else {
        // @ts-ignore
        this.$refs.paymentGateway.submitForm();
      }
    },

    addDiscount(val: string): void {
      this.validatingDiscount = true;
      this.setSubmitButtonDisableState(true);

      const params = {
        cartToken: this.cartToken,
        code: val.replace(/[/\\]/g, '|')
      };

      this.validateDiscountCode(params)
        .then(({ orderTotals, items, bundle }: ParsedDiscount) => {
          if (bundle) {
            this.commitCartBundle({
              ...bundle,
              items: [...items.filter((item) => item.bundle_id === bundle.id)],
            });
          }
          this.$emit('updateItems', items);
          this.$emit('updateOrderTotals', orderTotals);
          this.handleWindcaveReload();
          this.discountError = '';

          if (!this.displayWindcaveGateway) {
            this.setSubmitButtonDisableState(false);
          }
        })
        .catch((e: Error) => {
          this.$emit('handleAddDiscountError');
          const errorResponse = JSON.parse(e.message);
          this.discountError = errorResponse.body.message;
          this.setSubmitButtonDisableState(false);
        })
        .finally(() => {
          this.validatingDiscount = false;
        });
    },

    removeDiscount(val: string): void {
      this.validatingDiscount = true;
      this.setSubmitButtonDisableState(true);

      const params = {
        cartToken: this.cartToken,
        code: val
      };

      this.removeDiscountCode(params)
        .then(
          ({ orderTotals, items, bundle }: ParsedDiscount) => {
            if (bundle) {
              this.commitCartBundle({
                ...bundle,
                items: [...items.filter((item) => item.bundle_id === bundle.id)],
              });
            }
            this.$emit('updateItems', items);
            this.$emit('updateOrderTotals', orderTotals);
            this.handleWindcaveReload();

            this.discountError = '';
          }
        )
        .catch((e: Error) => {
          const errorResponse = JSON.parse(e.message);
          this.discountError = errorResponse.body.message;
        })
        .finally(() => {
          this.validatingDiscount = false;

          if (!this.displayWindcaveGateway) {
            this.setSubmitButtonDisableState(false);
          }
        });
    },

    errorHandler(): void {
      this.setSubmitButtonDisableState(false);
      this.$emit('error');
      this.handleWindcaveReload();
    },

    /**
     * For Windcave, whenever a discount is added or removed,
     * it's mandatory to send a new client-config request with the updated price.
     * This ensures that a new submitCardLink is received with reloading the payment widget
     */
    handleWindcaveReload(): void {
      if (this.displayWindcaveGateway) {
        this.$emit('reloadWindcave');
      }
    },

    addCancellationProtection(): void {
      this.$emit('add-cancellation-protection');

      if (this.displayWindcaveGateway) {
        this.setSubmitButtonDisableState(true);
      }
    },

    removeCancellationProtection(): void {
      this.$emit('remove-cancellation-protection');

      if (this.displayWindcaveGateway) {
        this.setSubmitButtonDisableState(true);
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    checkSuccess(validation: any, value: string | number | null, isRequired: boolean): boolean {
      if (isRequired) return validation.$anyDirty && !validation.$anyError;
      /**
      * For fields with only requiredIf=false validation (ex. zipcode) - validation.$anyError will always be false.
      * Extra check for the field value is required to prevent displaying the success state for an empty field.
      */
      return validation.$anyDirty && !validation.$anyError && !!value;
    },

    /**
     * Updates the state of submit button
     * true - the button is disabled
     * false - the button is enabled
     * @param {boolean} value
     */
    setSubmitButtonDisableState(value: boolean): void {
      this.disableButton = value;
    },

    getSearchCriteria(direction: string): string {
      const date = direction === 'arrival'
        ? this.readCartItems[0].search_criteria[this.cartItemsType].date2
        : this.readCartItems[0].search_criteria[this.cartItemsType].date1;
      const time = direction === 'arrival'
        ? this.readCartItems[0].search_criteria[this.cartItemsType].time2
        : this.readCartItems[0].search_criteria[this.cartItemsType].time1;
      const airportCode = this.readCartItems[0].search_criteria[this.cartItemsType].airport_code;
      const dateTimeISO = `${date}T${time}:00.000Z`;
      const zonedDate = utcToZonedTime(new Date(dateTimeISO), 'UTC');
      return {
        datetime: format(zonedDate, DATE_TIME_FORMATS.year_month_day_utc),
        airportCode,
      };
    },

    async vehicleLookUp() {
      this.vehicleLookUpError = false;
      // Validate license plate
      this.$v.userDetails.license_plate.$touch();
      if (this.$v.userDetails.license_plate.$error) return;
      this.vehicleLookUpLoading = true;
      this.enterManually = false;
      this.showDetails = false;
      try {
        const details = await this.fetchVehicleDetails(this.userDetails.license_plate);
        this.userDetails.vehicle_make = details.make;
        this.userDetails.vehicle_model = details.model;
        this.userDetails.vehicle_colour = details.colour;
        this.showDetails = true;
        this.enterManually = false;
      } catch (error) {
        this.vehicleLookUpError = true;
        datadogRum.addError(error);
        this.showDetails = false;
        this.enterManually = true;
      } finally {
        this.vehicleLookUpLoading = false;
      }
    },
  }
});
